import { decodeJwt } from '@stayon/auth-core';

/**
 * Das Token der Support-Sitzung eines Tabs. `renew` holt mit der Heimatsitzung ein neues und
 * liefert `null`, sobald die Sitzung, die `stale` ausgestellt hat, nicht mehr die laufende dieses
 * Tabs ist — dann darf der Request weder wiederholt noch mit einem anderen Token geschickt werden.
 */
export interface SupportTokenSource {
  current(): string | null;
  renew(stale: string, rejected: boolean): Promise<string | null>;
}

/**
 * Ein Request für den Ziel-Workspace, dessen Support-Sitzung inzwischen endete. Er geht nicht mit
 * dem Heimat-Token raus: dort träfe er den eigenen Workspace statt den des Kunden.
 */
export class SupportSessionEndedError extends Error {
  constructor() {
    super('Die Support-Sitzung ist beendet. Die Anfrage wurde nicht gesendet.');
    this.name = 'SupportSessionEndedError';
  }
}

/**
 * Ein Request einer Identität, die der Tab inzwischen verlassen hat — etwa aus dem eigenen
 * Workspace, nachdem eine Support-Sitzung begann, oder nach einer Anmeldung als anderes Konto.
 */
export class IdentityChangedError extends Error {
  constructor() {
    super(
      'Dieser Tab arbeitet inzwischen in einem anderen Workspace. Die Anfrage wurde nicht gesendet.',
    );
    this.name = 'IdentityChangedError';
  }
}

export function bearerOf(request: Request): string | null {
  const header = request.headers.get('Authorization');
  return header?.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
}

/** Die Sitzung, für die ein Support-Token ausgestellt wurde; `null` für jedes andere Token. */
export function supportSessionOf(token: string | null): string | null {
  const claim = token ? decodeJwt(token)?.support_session_id : undefined;
  return typeof claim === 'string' ? claim : null;
}

/** Die Browser-Anmeldung, die ein Support-Token ausgestellt bekam. */
export function supportOwnerOf(token: string): string | null {
  const claims = decodeJwt(token);
  return claims?.sub &&
    typeof claims.home_tenant_id === 'string' &&
    typeof claims.session_family_id === 'string'
    ? `${claims.sub}|${claims.home_tenant_id}|${claims.session_family_id}`
    : null;
}
